import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronRight, MessageCircle } from 'lucide-react'
import { tokens } from '@/theme'
import type { Capability, InitiativeListItem, Lifecycle } from '@/domain/types'
import { useInitiatives } from '@/api/hooks/useInitiatives'
import { useCapabilities } from '@/api/hooks/useCapabilities'
import { useLookupStages, useLookupBusinessUnits } from '@/api/hooks/useLookups'
import { useLookupUsers } from '@/api/hooks/useLookupUsers'
import { useInitiativeStats, stageCount } from '@/api/hooks/useInitiativeStats'
import { OrionFunnel } from '@/components/visual/OrionFunnel'
import { SearchBox } from '@/components/layout/SearchBox'
import { Pagination } from '@/components/layout/Pagination'
import { MOBILE_MAX } from '@/components/layout/responsive'
import { SkeletonList, ErrorPanel } from '@/components/feedback'

/**
 * Initiatives — the funnel and the list on one page.
 *
 * Implements `Initiatives v2.dc.html`. The separate funnel board is gone: it
 * showed the shape of the portfolio in six columns, and this page shows the
 * same shape as counts in the hero. One page now answers both "what is the
 * shape of this" and "find me the thing", and there is no longer a board and
 * a list that can disagree.
 *
 * The hero carries the funnel itself, collapsed to a glyph and the counts.
 * Expanding it explains the process — the six stages, what each one is for,
 * and the gate at the end of it — which is the reference the board's
 * column headers used to be.
 *
 * Four deliberate departures from the design file:
 *
 *   - **Stage colours** come from `tokens`, not the design's own map. The
 *     design re-assigns the ramp (its Shaped is the app's Experiment blue);
 *     the app's ramp is already on home, the detail view and every pill, and
 *     one page disagreeing about what colour Pilot is would be worse than
 *     matching the mock exactly.
 *   - **"newest first"**, not the design's "sorted by next gate due". The
 *     list endpoint orders by id descending and printing a sort the API does
 *     not perform would be a lie in the UI. Sorting by gate due needs an
 *     `orderBy` param on `GET /api/initiatives` first.
 *   - **The hero counts stop at Pilot.** Scaling and Operate are the two
 *     stages where an initiative has left the funnel and become delivery or
 *     business-as-usual; they are still on the list and still in the stage
 *     filter, they just no longer take a slot in the headline. The stage
 *     filter proper is `StagePicker`, in the filter bar with the others.
 *   - **No New initiative button.** An initiative is shaped in a
 *     conversation or not at all, so chat is the only front door.
 */

interface Props {
  onSelectInitiative: (initiativeId: number) => void
  /** Hands a seeded question to chat and routes there. */
  onRaiseIdea: (question: string) => void
}

const PAGE_SIZE = 25

/** Domains and capabilities are bounded sets; one page holds the tree. */
const CAPABILITY_PAGE = 100

const RAISE_IDEA_PROMPT =
  'I have an idea for an AI initiative — help me shape it into something the funnel can assess.'

/**
 * The funnel, in prose.
 *
 * Static rather than read from `/api/lookup/stages`: the explainer is
 * editorial copy about a six-stage process that is fixed by design, and the
 * gate names mirror `GATE_TRANSITIONS` in the API's `domain/stages.ts`.
 * Display names are overridden from the lookup where it has loaded, so a
 * renamed stage shows its new label; the copy below it would need a human
 * either way.
 */
interface FunnelStage {
  code: string
  name: string
  detail: string
  gateCode: string | null
  gateName: string | null
}

const FUNNEL_STAGES: readonly FunnelStage[] = [
  {
    code: 'Idea',
    name: 'Idea',
    detail:
      'A problem, who feels it, and a guess at the value. Anyone in the business unit can submit. The record stays in Orion permanently, including when it is killed.',
    gateCode: 'G0',
    gateName: 'Qualify',
  },
  {
    code: 'Shaped',
    name: 'Shaped',
    detail:
      'Outcome and how it will be measured, what it is today, the data it needs, the governance position. The stage where thinking happens and money does not.',
    gateCode: 'G1',
    gateName: 'Approve to build',
  },
  {
    code: 'Experiment',
    name: 'Experiment',
    detail:
      'The thinnest thing that tests the riskiest assumption. Sandbox environment, scoped test data, four weeks by default with a hard stop.',
    gateCode: 'G2',
    gateName: 'Prove it works',
  },
  {
    code: 'Pilot',
    name: 'Pilot',
    detail:
      'A named cohort using it inside the real workflow, on production data. Six to eight weeks, measured against the baseline recorded at Gate 1.',
    gateCode: 'G3',
    gateName: 'Commit to scale',
  },
  {
    code: 'Scaling',
    name: 'Scaling',
    detail:
      'A delivery project, not an experiment. Hardened, monitored, documented. The receiving business function owns it from here.',
    gateCode: 'G4',
    gateName: 'Handover to run',
  },
  {
    code: 'Operate',
    name: 'Operate',
    detail:
      'Owned by the business, watched by the register and by FinOps. It appears in the live inventory, which is also the Article 50 register.',
    gateCode: null,
    gateName: null,
  },
] as const

/**
 * Stage ink for the list's stage mark, on paper. The same six hues
 * `StagePill` uses — this surface just wears them as a dot and a label
 * rather than a pill, which is what the design's dense table asks for.
 */
const STAGE_INK: Record<string, string> = {
  Idea: tokens.ink3,
  Shaped: tokens.statusOrange,
  Experiment: tokens.statusBlue,
  Pilot: tokens.statusTeal,
  Scaling: tokens.statusCyanInk,
  Operate: tokens.statusGreen,
}

/**
 * The stages the hero counts out.
 *
 * The funnel is six stages long, but the last two describe work that has
 * already left it — Scaling is a delivery project and Operate is the live
 * inventory. The headline is about the pipeline, so it stops where the
 * pipeline does. Both stages are still on the list and still selectable in
 * the stage filter.
 */
const HERO_STAGE_CODES: readonly string[] = ['Idea', 'Shaped', 'Experiment', 'Pilot']

/**
 * Media queries can't be expressed inline and the app carries no stylesheet,
 * so — as in App.tsx and HomeView — the few responsive rules live in a
 * scoped block. Everything else stays inline.
 */
const initiativesStyles = `
.orion-init-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px 130px 90px 20px;
  gap: 20px;
  align-items: center;
}
/* One row on a laptop: four filters and the search box, no labels — each
   control says what it filters in its own resting state ("All owners"). */
.orion-init-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.orion-init-search { display: block; flex: 0 1 240px; min-width: 170px; }
@media (max-width: 900px) {
  .orion-init-grid { grid-template-columns: minmax(0, 1fr) auto; }
  .orion-init-narrow-hide { display: none; }
  .orion-init-head { display: none; }
  .orion-init-mark { display: none; }
}
@media (max-width: 640px) {
  .orion-init-funnel-glyph { display: none; }
}
@media (max-width: ${MOBILE_MAX}px) {
  /* A grid rather than a ragged wrap. The two selects share a line; the two
     pickers take their own, because each one drops a panel that has to fit
     inside the viewport and it is measured against its trigger. */
  .orion-init-filters { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .orion-init-filters > * { width: 100%; min-width: 0; }
  .orion-init-filters select,
  .orion-init-filters > span > button { min-width: 0 !important; }
  .orion-init-picker,
  .orion-init-search { grid-column: 1 / -1; }
  .orion-init-spacer { display: none; }
  .orion-init-hero { padding-top: 18px !important; padding-bottom: 18px !important; }
}
`

export function InitiativesView({ onSelectInitiative, onRaiseIdea }: Props) {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [domainId, setDomainId] = useState<number | null>(null)
  const [capabilityId, setCapabilityId] = useState<number | null>(null)
  const [businessUnitCode, setBusinessUnitCode] = useState<string | 'all'>('all')
  const [ownerObjectId, setOwnerObjectId] = useState<string | 'all'>('all')
  const [stageCodes, setStageCodes] = useState<string[]>([])

  // Which stage the funnel is reading out. Independent of the filter: you
  // can read what Pilot means without filtering the list to it.
  const [activeStage, setActiveStage] = useState(0)
  const [funnelOpen, setFunnelOpen] = useState(false)

  const stats = useInitiativeStats('stage')
  const stagesQuery = useLookupStages()
  const businessUnitsQuery = useLookupBusinessUnits()
  const usersQuery = useLookupUsers()
  const capabilitiesQuery = useCapabilities({ page: 1, pageSize: CAPABILITY_PAGE })

  // Display names from the picklist where it has landed; the static order and
  // copy otherwise.
  const stages = useMemo<FunnelStage[]>(() => {
    if (stagesQuery.status !== 'success') return [...FUNNEL_STAGES]
    return FUNNEL_STAGES.map(stage => {
      const looked = stagesQuery.data.find(s => s.stageCode === stage.code)
      return looked ? { ...stage, name: looked.stageName } : stage
    })
  }, [stagesQuery])

  const capabilities = useMemo<Capability[]>(
    () => (capabilitiesQuery.status === 'success' ? capabilitiesQuery.data.data : []),
    [capabilitiesQuery],
  )
  const capabilitiesTruncated =
    capabilitiesQuery.status === 'success' && capabilitiesQuery.data.hasMore

  const initiatives = useInitiatives({
    page,
    pageSize: PAGE_SIZE,
    ...(q && { q }),
    ...(domainId !== null && { domainId }),
    ...(capabilityId !== null && { capabilityId }),
    ...(businessUnitCode !== 'all' && { businessUnitCode: [businessUnitCode] }),
    ...(ownerObjectId !== 'all' && { ownerObjectId }),
    ...(stageCodes.length > 0 && { stageCode: stageCodes }),
  })

  /** Every filter change resets to page 1 — page 4 of a new filter is nowhere. */
  const resetPage = () => setPage(1)

  const toggleStage = (code: string): void => {
    resetPage()
    setStageCodes(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]))
  }

  const filtered =
    q !== '' ||
    domainId !== null ||
    capabilityId !== null ||
    businessUnitCode !== 'all' ||
    ownerObjectId !== 'all' ||
    stageCodes.length > 0

  return (
    <>
      <style>{initiativesStyles}</style>

      <FunnelHero
        stages={stages}
        total={stats.status === 'success' ? stats.data.total : null}
        count={code => stageCount(stats, code)}
        active={activeStage}
        onActive={setActiveStage}
        open={funnelOpen}
        onToggleOpen={() => setFunnelOpen(open => !open)}
      />

      <div
        className="orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          width: '100%',
          margin: '0 auto',
          padding: `28px ${tokens.containerPadX}px 72px`,
          boxSizing: 'border-box',
        }}
      >
        <FilterBar
          q={q}
          onQ={next => {
            resetPage()
            setQ(next)
          }}
          businessUnitCode={businessUnitCode}
          onBusinessUnitCode={next => {
            resetPage()
            setBusinessUnitCode(next)
          }}
          businessUnits={businessUnitsQuery.status === 'success' ? businessUnitsQuery.data : []}
          ownerObjectId={ownerObjectId}
          onOwnerObjectId={next => {
            resetPage()
            setOwnerObjectId(next)
          }}
          users={usersQuery.status === 'success' ? usersQuery.data.data : []}
          stages={stages}
          stageCodes={stageCodes}
          onToggleStage={toggleStage}
          onClearStages={() => {
            resetPage()
            setStageCodes([])
          }}
          capabilities={capabilities}
          capabilitiesTruncated={capabilitiesTruncated}
          domainId={domainId}
          capabilityId={capabilityId}
          onPickDomain={next => {
            resetPage()
            setDomainId(next)
            setCapabilityId(null)
          }}
          onPickCapability={(nextDomainId, nextCapabilityId) => {
            resetPage()
            setDomainId(nextDomainId)
            setCapabilityId(nextCapabilityId)
          }}
          onClearCapability={() => {
            resetPage()
            setDomainId(null)
            setCapabilityId(null)
          }}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 14,
          }}
        >
          <span
            style={{
              fontFamily: tokens.fontDisplay,
              fontSize: 12,
              color: tokens.ink3,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {initiatives.status === 'success' ? (
              <>
                Showing {initiatives.data.data.length} of {initiatives.data.total}
                {filtered ? ' matching' : ''} · sorted{' '}
                <b style={{ color: tokens.ink, fontWeight: tokens.weightDemi }}>newest first</b>
              </>
            ) : (
              'Loading initiatives…'
            )}
          </span>

          <RaiseIdeaButton onClick={() => onRaiseIdea(RAISE_IDEA_PROMPT)} />
        </div>

        <div
          className="orion-init-grid orion-init-head"
          style={{
            padding: '0 20px 10px',
            borderBottom: `1px solid ${tokens.line}`,
            fontFamily: tokens.fontDisplay,
            fontSize: 10,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: tokens.ink4,
          }}
        >
          <span>Initiative</span>
          <span>Owner</span>
          <span>Stage</span>
          <span>Business unit</span>
          <span />
        </div>

        {initiatives.status !== 'success' && initiatives.status !== 'error' && (
          <div style={{ marginTop: 8 }}>
            <SkeletonList rows={6} rowHeight={72} />
          </div>
        )}
        {initiatives.status === 'error' && <ErrorPanel error={initiatives.error} />}
        {initiatives.status === 'success' && (
          <>
            {initiatives.data.data.length === 0 ? (
              <EmptyState filtered={filtered} />
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  display: 'grid',
                  gap: 8,
                  padding: 0,
                  margin: '8px 0 0',
                }}
              >
                {initiatives.data.data.map(initiative => (
                  <InitiativeRow
                    key={initiative.initiativeId}
                    initiative={initiative}
                    onClick={() => onSelectInitiative(initiative.initiativeId)}
                  />
                ))}
              </ul>
            )}
            <Pagination
              page={initiatives.data.page}
              pageSize={initiatives.data.pageSize}
              total={initiatives.data.total}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Hero — the funnel, its six counts, and the explainer behind them
// ---------------------------------------------------------------------------

interface HeroProps {
  stages: FunnelStage[]
  total: number | null
  count: (stageCode: string) => number | null
  active: number
  onActive: (index: number) => void
  open: boolean
  onToggleOpen: () => void
}

function FunnelHero(props: HeroProps) {
  return (
    <section
      style={{
        position: 'relative',
        background: tokens.inkPanel,
        borderBottom: `1px solid ${tokens.inkPanelLine}`,
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 32% 60%, rgba(153,130,66,0.20) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="orion-init-hero orion-pad"
        style={{
          position: 'relative',
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `16px ${tokens.containerPadX}px`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 26,
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: '0 1 auto' }}>
          {/* The glyph is the same funnel as the panel's, at a glance size.
              Clicking a slab opens the explainer on that stage. */}
          <div className="orion-init-funnel-glyph" style={{ width: 132, flex: 'none' }}>
            <OrionFunnel
              active={props.active}
              height={104}
              count={props.stages.length}
              onSelect={index => {
                props.onActive(index)
                if (!props.open) props.onToggleOpen()
              }}
            />
          </div>

          <div>
            <div
              style={{
                width: 32,
                height: 2,
                background: tokens.gold,
                marginBottom: 10,
                transformOrigin: 'left',
                animation: `barGrow ${tokens.durSlow} ${tokens.easeStandard} both`,
              }}
            />
            <div
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 10,
                fontWeight: tokens.weightDemi,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: tokens.inkPanelText2,
                marginBottom: 8,
              }}
            >
              AI Initiatives
            </div>
            <h1
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 26,
                fontWeight: tokens.weightDemi,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                color: tokens.paper,
                margin: 0,
              }}
            >
              <span style={{ opacity: 0.5 }}>Every initiative, </span>in one place.
            </h1>
            <p
              style={{
                fontFamily: tokens.fontDisplay,
                fontSize: 12.5,
                color: tokens.inkPanelText2,
                margin: '6px 0 0',
                maxWidth: '38ch',
              }}
            >
              Filter by stage, capability or owner — or open the funnel to see how an initiative
              moves.
            </p>
          </div>
        </div>

        {/* Named as a group: a screen reader landing on "72 All" needs to
            know what it is a count of. These read out the portfolio; the
            controls that narrow it are in the filter bar below. */}
        <div
          role="group"
          aria-label="Initiatives by stage"
          className="orion-init-counts"
          style={{ flex: '0 1 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}
        >
          <StageCount label="All" value={props.total} first />
          {props.stages
            .filter(stage => HERO_STAGE_CODES.includes(stage.code))
            .map(stage => (
              <StageCount key={stage.code} label={stage.name} value={props.count(stage.code)} />
            ))}
        </div>

        <FunnelToggle open={props.open} onClick={props.onToggleOpen} />
      </div>

      {props.open && (
        <FunnelExplainer
          stages={props.stages}
          count={props.count}
          active={props.active}
          onActive={props.onActive}
        />
      )}
    </section>
  )
}

/**
 * One figure in the hero's read-out. Not a control: stage selection is a
 * filter like every other filter, and lives in the bar over the list.
 */
function StageCount({
  label,
  value,
  first,
}: {
  label: string
  value: number | null
  first?: boolean
}) {
  return (
    <div
      style={{
        textAlign: 'left',
        borderLeft: first ? 'none' : `1px solid ${tokens.inkPanel2}`,
        padding: first ? '8px 18px 8px 0' : '8px 18px',
      }}
    >
      <div
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 21,
          fontWeight: tokens.weightDemi,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          color: tokens.inkPanelText3,
        }}
      >
        {value === null ? '—' : value}
      </div>
      <div
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.inkPanelText2,
          marginTop: 6,
        }}
      >
        {label}
      </div>
    </div>
  )
}

function FunnelToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        background: 'transparent',
        border: `1px solid ${hover ? tokens.gold : tokens.inkPanelLine}`,
        borderRadius: tokens.radiusMd,
        padding: '9px 16px',
        fontFamily: tokens.fontDisplay,
        fontSize: 11,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: hover ? tokens.paper : tokens.inkPanelText,
        cursor: 'pointer',
        transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      {open ? 'Hide the funnel' : 'How the funnel works'}
      <ChevronDown
        size={12}
        strokeWidth={2}
        color={tokens.gold}
        style={{
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: `transform ${tokens.durStd} ${tokens.easeStandard}`,
        }}
      />
    </button>
  )
}

function FunnelExplainer({
  stages,
  count,
  active,
  onActive,
}: {
  stages: FunnelStage[]
  count: (stageCode: string) => number | null
  active: number
  onActive: (index: number) => void
}) {
  return (
    <div
      role="region"
      aria-label="How the funnel works"
      className="orion-pad"
      style={{
        position: 'relative',
        maxWidth: tokens.containerMax,
        margin: '0 auto',
        padding: `6px ${tokens.containerPadX}px 36px`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 36,
        alignItems: 'flex-start',
        borderTop: `1px solid ${tokens.inkPanel2}`,
      }}
    >
      <div
        className="orion-init-mark"
        style={{
          flex: '1 1 320px',
          minWidth: 280,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 18,
        }}
      >
        <OrionFunnel
          active={active}
          height={410}
          count={stages.length}
          onSelect={index => onActive(index)}
        />
        <div
          style={{
            fontFamily: tokens.fontDisplay,
            fontSize: 11,
            color: tokens.inkPanelText2,
            marginTop: 6,
            textAlign: 'center',
          }}
        >
          Select a stage to read its gate
        </div>
      </div>

      <div style={{ flex: '1 1 340px', minWidth: 300, paddingTop: 18 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {stages.map((stage, index) => {
            const on = index === active
            return (
              <li key={stage.code}>
                <button
                  type="button"
                  aria-expanded={on}
                  onClick={() => onActive(index)}
                  onMouseEnter={() => onActive(index)}
                  onFocus={() => onActive(index)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: 14,
                    alignItems: 'start',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: '9px 14px',
                    background: on ? tokens.goldWash : 'transparent',
                    border: 'none',
                    borderLeft: `2px solid ${on ? tokens.gold : tokens.inkPanelLine}`,
                    fontFamily: tokens.fontDisplay,
                    transition: `background ${tokens.durStd} ${tokens.easeStandard}`,
                  }}
                >
                  <span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: tokens.weightDemi,
                        letterSpacing: '0.02em',
                        color: on ? tokens.paper : tokens.inkPanelText3,
                      }}
                    >
                      {stage.name}
                    </span>
                    {on && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12.5,
                          lineHeight: 1.55,
                          color: tokens.inkPanelText,
                          marginTop: 8,
                        }}
                      >
                        {stage.detail}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: tokens.weightDemi,
                      fontVariantNumeric: 'tabular-nums',
                      color: on ? tokens.gold : tokens.inkPanelText2,
                    }}
                  >
                    {count(stage.code) ?? '—'}
                  </span>
                </button>

                {on && stage.gateName && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                      padding: '9px 14px',
                      marginLeft: 2,
                    }}
                  >
                    <span
                      style={{
                        flex: 'none',
                        fontFamily: tokens.fontDisplay,
                        fontSize: 10.5,
                        fontWeight: tokens.weightDemi,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: tokens.goldSoft,
                      }}
                    >
                      Gate {stage.gateCode} · {stage.gateName}
                    </span>
                    <span style={{ flex: 1, borderTop: `1px dashed ${tokens.inkPanelLine}` }} />
                  </div>
                )}
              </li>
            )
          })}
        </ul>

        <div
          style={{
            marginTop: 18,
            borderLeft: `2px solid ${tokens.gold}`,
            padding: '2px 0 2px 14px',
          }}
        >
          <p
            style={{
              fontFamily: tokens.fontDisplay,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: tokens.inkPanelText2,
              margin: 0,
            }}
          >
            Every gate can advance, park, salvage or close. An exit at any gate still leaves a
            reusable asset.
          </p>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

interface FilterBarProps {
  q: string
  onQ: (next: string) => void
  businessUnitCode: string | 'all'
  onBusinessUnitCode: (next: string | 'all') => void
  businessUnits: { businessUnitCode: string; businessUnitName: string }[]
  ownerObjectId: string | 'all'
  onOwnerObjectId: (next: string | 'all') => void
  users: { entraObjectId: string; displayName: string; userPrincipalName: string }[]
  stages: FunnelStage[]
  stageCodes: string[]
  onToggleStage: (code: string) => void
  onClearStages: () => void
  capabilities: Capability[]
  capabilitiesTruncated: boolean
  domainId: number | null
  capabilityId: number | null
  onPickDomain: (domainId: number) => void
  onPickCapability: (domainId: number, capabilityId: number) => void
  onClearCapability: () => void
}

/**
 * Every filter on one line at laptop width.
 *
 * The labels that used to sit in front of each control are gone: four
 * captions and their gaps were most of a second row, and each control already
 * says what it filters when nothing is chosen ("All owners"). The captions
 * survive as `aria-label`, so nothing is lost to a screen reader.
 */
function FilterBar(props: FilterBarProps) {
  return (
    <div className="orion-init-filters">
      <StagePicker
        stages={props.stages}
        selected={props.stageCodes}
        onToggle={props.onToggleStage}
        onClear={props.onClearStages}
      />

      <CapabilityPicker
        capabilities={props.capabilities}
        truncated={props.capabilitiesTruncated}
        domainId={props.domainId}
        capabilityId={props.capabilityId}
        onPickDomain={props.onPickDomain}
        onPickCapability={props.onPickCapability}
        onClear={props.onClearCapability}
      />

      <Select
        value={props.businessUnitCode === 'all' ? '' : props.businessUnitCode}
        onChange={v => props.onBusinessUnitCode(v === '' ? 'all' : v)}
        minWidth={150}
        disabled={props.businessUnits.length === 0}
        label="Business unit"
      >
        <option value="">All units</option>
        {props.businessUnits.map(bu => (
          <option key={bu.businessUnitCode} value={bu.businessUnitCode}>
            {bu.businessUnitName}
          </option>
        ))}
      </Select>

      <Select
        value={props.ownerObjectId === 'all' ? '' : props.ownerObjectId}
        onChange={v => props.onOwnerObjectId(v === '' ? 'all' : v)}
        minWidth={150}
        disabled={props.users.length === 0}
        label="Owner"
      >
        <option value="">All owners</option>
        {props.users.map(u => (
          <option key={u.entraObjectId} value={u.entraObjectId}>
            {u.displayName || u.userPrincipalName}
          </option>
        ))}
      </Select>

      <span className="orion-init-spacer" style={{ flex: 1 }} />
      <span className="orion-init-search">
        <SearchBox value={props.q} onChange={props.onQ} placeholder="Search initiatives…" fluid />
      </span>
    </div>
  )
}

/**
 * Stage, as a filter.
 *
 * A popover of checkboxes rather than a `<select>` because stages stack: the
 * useful question is usually "what is in flight" — Experiment and Pilot
 * together — which a single-choice control can't ask. Same idiom as the
 * capability picker beside it, so the two read as one bar.
 */
function StagePicker({
  stages,
  selected,
  onToggle,
  onClear,
}: {
  stages: FunnelStage[]
  selected: string[]
  onToggle: (code: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const hostRef = useRef<HTMLSpanElement>(null)
  useDismissable(open, () => setOpen(false), hostRef)

  const label =
    selected.length === 0
      ? 'All stages'
      : selected.length === 1
        ? (stages.find(s => s.code === selected[0])?.name ?? selected[0]!)
        : `${selected.length} stages`

  return (
    <span
      ref={hostRef}
      className="orion-init-picker"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <PickerButton
        label={label}
        on={selected.length > 0}
        open={open}
        ariaLabel={`Stage filter: ${label}`}
        minWidth={150}
        onClick={() => setOpen(o => !o)}
      />

      {open && (
        <div style={pickerPanelStyle}>
          <PickerRow
            label="All stages"
            onClick={() => {
              onClear()
              setOpen(false)
            }}
            tone="muted"
          />
          {stages.map(stage => (
            <PickerRow
              key={stage.code}
              label={stage.name}
              selected={selected.includes(stage.code)}
              onClick={() => onToggle(stage.code)}
              tone="capability"
              checkable
            />
          ))}
        </div>
      )}
    </span>
  )
}

/**
 * Domain → capability, in one control.
 *
 * The design replaces the old pair of cascading selects with a single tree:
 * picking a domain filters to the whole domain, picking a capability under
 * it narrows further, and the counts say where the work actually is before
 * you commit to a filter. Counts are `initiativeCount` from
 * `GET /api/capabilities` — portfolio-wide, not "of the rows on screen".
 */
function CapabilityPicker({
  capabilities,
  truncated,
  domainId,
  capabilityId,
  onPickDomain,
  onPickCapability,
  onClear,
}: {
  capabilities: Capability[]
  truncated: boolean
  domainId: number | null
  capabilityId: number | null
  onPickDomain: (domainId: number) => void
  onPickCapability: (domainId: number, capabilityId: number) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const hostRef = useRef<HTMLSpanElement>(null)
  useDismissable(open, () => setOpen(false), hostRef)

  const tree = useMemo(() => {
    const byDomain = new Map<number, { domainName: string; caps: Capability[] }>()
    for (const capability of capabilities) {
      const entry = byDomain.get(capability.domainId)
      if (entry) entry.caps.push(capability)
      else
        byDomain.set(capability.domainId, { domainName: capability.domainName, caps: [capability] })
    }
    return [...byDomain.entries()]
      .map(([id, entry]) => ({
        domainId: id,
        domainName: entry.domainName,
        count: entry.caps.reduce((sum, c) => sum + c.initiativeCount, 0),
        caps: [...entry.caps].sort(
          (a, b) =>
            a.capabilityLevel - b.capabilityLevel ||
            a.capabilityName.localeCompare(b.capabilityName),
        ),
      }))
      .sort((a, b) => a.domainName.localeCompare(b.domainName))
  }, [capabilities])

  const selectedDomain = tree.find(d => d.domainId === domainId)
  const selectedCapability = capabilities.find(c => c.capabilityId === capabilityId)
  const label = selectedCapability
    ? `${selectedCapability.domainName} › ${selectedCapability.capabilityName}`
    : selectedDomain
      ? `${selectedDomain.domainName} › All`
      : 'All capabilities'

  return (
    <span
      ref={hostRef}
      className="orion-init-picker"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <PickerButton
        label={label}
        on={domainId !== null}
        open={open}
        ariaLabel={`Capability filter: ${label}`}
        minWidth={220}
        onClick={() => setOpen(o => !o)}
      />

      {open && (
        <div style={pickerPanelStyle}>
          <PickerRow
            label="All capabilities"
            onClick={() => {
              onClear()
              setOpen(false)
            }}
            tone="muted"
          />
          {tree.map(domain => (
            <div key={domain.domainId}>
              <PickerRow
                label={domain.domainName}
                count={domain.count}
                selected={domain.domainId === domainId && capabilityId === null}
                onClick={() => {
                  onPickDomain(domain.domainId)
                  setOpen(false)
                }}
                tone="domain"
              />
              {domain.caps.map(capability => (
                <PickerRow
                  key={capability.capabilityId}
                  label={capability.capabilityName}
                  count={capability.initiativeCount}
                  selected={capability.capabilityId === capabilityId}
                  indent={capability.capabilityLevel}
                  onClick={() => {
                    onPickCapability(domain.domainId, capability.capabilityId)
                    setOpen(false)
                  }}
                  tone="capability"
                />
              ))}
            </div>
          ))}
          {tree.length === 0 && <PickerRow label="No capabilities loaded" tone="muted" />}
          {truncated && (
            <div
              style={{
                padding: '9px 14px',
                fontFamily: tokens.fontDisplay,
                fontSize: 11,
                color: tokens.ink4,
                borderTop: `1px solid ${tokens.line}`,
              }}
            >
              First {CAPABILITY_PAGE} capabilities only — search narrows the list instead.
            </div>
          )}
        </div>
      )}
    </span>
  )
}

function PickerRow({
  label,
  count,
  selected,
  indent = 0,
  tone,
  checkable,
  onClick,
}: {
  label: string
  count?: number
  selected?: boolean
  indent?: number
  tone: 'domain' | 'capability' | 'muted'
  /** Renders the row as a multi-select option and reports its state. */
  checkable?: boolean
  onClick?: () => void
}) {
  const [hover, setHover] = useState(false)
  const color = tone === 'muted' ? tokens.ink3 : tone === 'domain' ? tokens.ink : tokens.ink2

  return (
    <button
      type="button"
      disabled={!onClick}
      {...(checkable ? { role: 'checkbox', 'aria-checked': Boolean(selected) } : {})}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: indent === 0 ? '9px 14px' : `7px 14px 7px ${16 + indent * 14}px`,
        background: hover ? tokens.paper2 : selected ? tokens.goldWash : 'transparent',
        border: 'none',
        borderLeft: `2px solid ${selected && tone === 'capability' ? tokens.gold : 'transparent'}`,
        borderBottom: tone === 'muted' ? `1px solid ${tokens.paper3}` : 'none',
        fontFamily: tokens.fontDisplay,
        fontSize: 12,
        fontWeight: tone === 'domain' ? tokens.weightDemi : tokens.weightBook,
        color: selected ? tokens.goldDark : color,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {checkable && (
          <span
            aria-hidden
            style={{
              flex: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 13,
              height: 13,
              borderRadius: 2,
              border: `1px solid ${selected ? tokens.gold : tokens.line2}`,
              background: selected ? tokens.gold : tokens.paper,
              color: tokens.paper,
            }}
          >
            {selected && <Check size={9} strokeWidth={3} />}
          </span>
        )}
        {label}
      </span>
      {count !== undefined && (
        <span
          style={{
            flex: 'none',
            fontSize: 11,
            color: tokens.ink4,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
    </button>
  )
}

/**
 * The trigger both pickers wear: the current selection, ellipsised, with the
 * gold border every other filter uses to say "this one is narrowing things".
 */
function PickerButton({
  label,
  on,
  open,
  ariaLabel,
  minWidth,
  onClick,
}: {
  label: string
  on: boolean
  open: boolean
  ariaLabel: string
  minWidth: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        justifyContent: 'space-between',
        width: '100%',
        minWidth,
        background: tokens.paper,
        border: `1px solid ${on ? tokens.gold : tokens.line2}`,
        borderRadius: tokens.radiusMd,
        padding: '7px 12px',
        fontFamily: tokens.fontDisplay,
        fontSize: 12,
        color: tokens.ink,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={label}
      >
        {label}
      </span>
      <ChevronDown size={12} strokeWidth={1.8} color={tokens.ink4} />
    </button>
  )
}

const pickerPanelStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  zIndex: 50,
  // Never narrower than the trigger it hangs off, so a full-width picker on
  // a phone drops a full-width panel rather than one overhanging the edge.
  minWidth: '100%',
  width: 290,
  maxHeight: 340,
  overflow: 'auto',
  background: tokens.paper,
  border: `1px solid ${tokens.line2}`,
  boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
}

/**
 * Close on an outside click or Escape — a dropdown that only closes by
 * re-clicking its own trigger traps the pointer.
 */
function useDismissable(
  open: boolean,
  close: () => void,
  hostRef: React.RefObject<HTMLElement>,
): void {
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
    // `close` is a fresh closure each render; the effect only needs to be
    // re-bound when the dropdown opens or shuts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hostRef])
}

function Select({
  value,
  onChange,
  minWidth,
  disabled,
  label,
  children,
}: {
  value: string
  onChange: (next: string) => void
  minWidth: number
  disabled?: boolean
  label: string
  children: React.ReactNode
}) {
  return (
    <select
      value={value}
      aria-label={label}
      onChange={e => onChange(e.currentTarget.value)}
      disabled={disabled}
      style={{
        padding: '7px 12px',
        fontSize: 12,
        fontFamily: tokens.fontDisplay,
        color: disabled ? tokens.ink4 : tokens.ink,
        background: tokens.paper,
        border: `1px solid ${value === '' ? tokens.line2 : tokens.gold}`,
        borderRadius: tokens.radiusMd,
        // A native select is as wide as its widest option unless told
        // otherwise, and one long business-unit name would push the bar onto
        // a second row. Capped, and the label ellipsises instead.
        maxWidth: 200,
        cursor: disabled ? 'not-allowed' : 'pointer',
        minWidth,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </select>
  )
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function InitiativeRow({
  initiative,
  onClick,
}: {
  initiative: InitiativeListItem
  onClick: () => void
}) {
  const [hover, setHover] = useState(false)

  return (
    <li>
      <button
        className="orion-init-grid"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '16px 20px',
          background: hover ? tokens.paper2 : tokens.paper,
          border: `1px solid ${hover ? tokens.line2 : tokens.line}`,
          cursor: 'pointer',
          fontFamily: tokens.fontDisplay,
          transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 15,
              fontWeight: tokens.weightDemi,
              color: tokens.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: initiative.lifecycle === 'closed' ? 'line-through' : 'none',
            }}
          >
            {initiative.initiativeName}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 12,
              color: tokens.ink3,
              marginTop: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {initiative.domainName} · {initiative.capabilityName}
          </span>
        </span>

        <span
          className="orion-init-narrow-hide"
          style={{
            fontSize: 12,
            color: tokens.ink3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {initiative.ownerDisplayName || '—'}
        </span>

        <StageMark
          stageCode={initiative.stageCode}
          stageName={initiative.stageName}
          lifecycle={initiative.lifecycle}
        />

        {/* The display code, not the finance code: `M&I` is what people call
            the unit, and `IMI` is what the ledger calls it. The full name
            rides along as the tooltip. */}
        <span
          className="orion-init-narrow-hide"
          title={initiative.businessUnitName}
          style={{
            fontSize: 11.5,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.06em',
            color: tokens.ink2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {initiative.businessUnitDisplayCode || '—'}
        </span>

        <ChevronRight
          className="orion-init-narrow-hide"
          size={16}
          strokeWidth={1.6}
          color={hover ? tokens.gold : tokens.ink4}
        />
      </button>
    </li>
  )
}

/**
 * Stage as a dot and a label, per the design's table.
 *
 * Blocked and closed override the stage tone and say so, the same precedence
 * `StagePill` applies: where an initiative *is* matters less than the fact
 * that it stopped, and the stage stays visible so the list still shows where
 * it died.
 */
function StageMark({
  stageCode,
  stageName,
  lifecycle,
}: {
  stageCode: string
  stageName: string
  lifecycle: Lifecycle
}) {
  const color =
    lifecycle === 'closed'
      ? tokens.statusRed
      : lifecycle === 'blocked'
        ? tokens.statusAmber
        : (STAGE_INK[stageCode] ?? tokens.ink3)

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color,
        minWidth: 0,
      }}
    >
      <i
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: color,
          display: 'inline-block',
          flex: 'none',
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {stageName || stageCode}
        {lifecycle !== 'active' && ` · ${lifecycle}`}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

function RaiseIdeaButton({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        background: hover ? tokens.goldDark : tokens.gold,
        color: tokens.paper,
        border: 'none',
        borderRadius: tokens.radiusMd,
        padding: '11px 20px',
        fontSize: 11.5,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        fontFamily: tokens.fontDisplay,
        cursor: 'pointer',
        transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <MessageCircle size={14} strokeWidth={1.8} /> Raise an idea in chat
    </button>
  )
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div
      style={{
        margin: '8px 0 0',
        padding: '34px 24px',
        textAlign: 'center',
        color: tokens.ink3,
        fontFamily: tokens.fontDisplay,
        fontSize: 13,
        border: `1px dashed ${tokens.line2}`,
      }}
    >
      {filtered
        ? 'No initiatives match this filter.'
        : 'No initiatives yet — they will appear here once created.'}
    </div>
  )
}
